import { Drawer, Loading } from '@/components';
import { rateByEvaluationDirection } from '@/features/mold/utils';
import {
  useConsecutiveIndicators,
  useUpdateConsecutiveIndicator,
} from '@/features/project/_hooks/activity';
import { useProject } from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { UpdateConsecutiveIndicatorFormSchema } from '../../../_models/activity';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { UpdateConsecutiveIndicatorForm } from './UpdateConsecutiveIndicatorForm';

type FormValues = z.infer<typeof UpdateConsecutiveIndicatorFormSchema>;

type Props = {
  fiscalYear: number;
};

export const ConsecutiveIndicatorFormDrawer: FC<Props> = ({ fiscalYear }) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();

  const project = useProject(projectId);

  const isOpen =
    drawerState.open &&
    drawerState.type === 'activity-consecutive-indicator-form';
  const isEdit = isOpen && drawerState.isEditing;
  const indicatorId = isEdit ? drawerState.indicatorId : '';
  const consecutiveIndicators = useConsecutiveIndicators(indicatorId, {
    enabled: isEdit,
  });

  const { mutateAsync: updateConsecutiveIndicatorAsync } =
    useUpdateConsecutiveIndicator();

  const originIndicator = consecutiveIndicators.data?.find(
    (indicator) => indicator.id === indicatorId,
  );

  const initialTargetYears = useMemo(() => {
    if (
      originIndicator?.type === 'outcome' &&
      originIndicator.dataNatureType === 'quantitative'
    ) {
      if (isEdit) {
        const editTargetYears = consecutiveIndicators.data?.flatMap(
          (indicator) => (indicator.isTargetYear ? [indicator.fiscalYear] : []),
        );

        return editTargetYears?.length ? editTargetYears : [fiscalYear];
      }
      return [fiscalYear];
    }
    return [];
  }, [isEdit, consecutiveIndicators.data, fiscalYear, originIndicator]);

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  const initialValues = useMemo<Partial<FormValues> | undefined>(() => {
    if (!consecutiveIndicators.data?.length || !originIndicator) {
      return undefined;
    }

    const annualValueIdByProjectFiscalYear = consecutiveIndicators.data?.find(
      (v) => v.fiscalYear === fiscalYear,
    )?.id;

    const isOutcomeQualitative =
      originIndicator.type === 'outcome' &&
      originIndicator.dataNatureType === 'qualitative';

    return {
      annualValues: isOutcomeQualitative
        ? [
            {
              id: annualValueIdByProjectFiscalYear,
              year: fiscalYear,
              actualValue: null,
              targetValue: null,
              achievementRate: null,
              isManualAchievementRate: false,
            },
          ]
        : consecutiveIndicators.data.map((annualValue) => {
            const manualAchievementRate = rateByEvaluationDirection({
              evaluationDirection: originIndicator?.evaluationDirection,
              targetValue: annualValue.targetValue,
              actualValue: annualValue.actualValue,
            });

            return {
              id: annualValue.id,
              year: annualValue.fiscalYear,
              actualValue: annualValue.actualValue ?? null,
              targetValue: annualValue.targetValue,
              achievementRate:
                annualValue.achievementRate ?? manualAchievementRate,
              isManualAchievementRate: annualValue.achievementRate !== null,
            };
          }),
      title: originIndicator.title,
      type: originIndicator.type,
      term: originIndicator.term,
      unit: originIndicator.unit,
      dataNatureType: originIndicator.dataNatureType,
      statusOfAchievementWhenQualitative: isOutcomeQualitative
        ? originIndicator.statusOfAchievementWhenQualitative
        : null,
    };
  }, [consecutiveIndicators.data, originIndicator, fiscalYear]);

  const handleSubmit = async (
    values: FormValues & { targetYears: number[] },
  ) => {
    await updateConsecutiveIndicatorAsync({
      ...values,
      baseIndicatorId: indicatorId,
    });
    await Promise.all([
      openMessage(
        'success',
        indicatorId
          ? t('message-update-completed')
          : t('message-create-completed'),
      ),
      handleClose(),
    ]);
  };

  if (
    isEdit &&
    (consecutiveIndicators.isRefetching || consecutiveIndicators.isLoading)
  ) {
    return <Loading />;
  }

  return (
    <Drawer size="large" open={isOpen} onClose={handleClose} destroyOnClose>
      <UpdateConsecutiveIndicatorForm
        evaluationDirection={originIndicator?.evaluationDirection}
        fiscalYear={fiscalYear}
        initialTargetYears={initialTargetYears}
        projectCategory={project.data?.category}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      />
    </Drawer>
  );
};
